import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Tooltip as RechartsTooltip } from 'recharts';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { adminApi } from '../services/api';
import { GrowthAnalytics, GrowthData } from '../types';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';

interface GrowthChartProps {
  isVisible?: boolean;
}

const GrowthChart: React.FC<GrowthChartProps> = ({ isVisible = true }) => {
  const [growthData, setGrowthData] = useState<GrowthAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState('30d');

  const fetchGrowthData = async (selectedPeriod: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getGrowthAnalytics(selectedPeriod);
      setGrowthData(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Büyüme verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Use visibility refresh hook
  const { lastRefresh } = useVisibilityRefresh(() => fetchGrowthData(period), {
    refreshInterval: 75000, // 75 seconds
    isVisible,
    enabled: true,
  });

  useEffect(() => {
    fetchGrowthData(period);
  }, [period]);

  const handlePeriodChange = (event: SelectChangeEvent) => {
    setPeriod(event.target.value);
  };

  const formatChartData = () => {
    if (!growthData) return [];

    // Create a map for easy lookup
    const userGrowthMap = new Map();
    const petGrowthMap = new Map();

    growthData.userGrowth.forEach((item: GrowthData) => {
      userGrowthMap.set(item._id, item.count);
    });

    growthData.petGrowth.forEach((item: GrowthData) => {
      petGrowthMap.set(item._id, item.count);
    });

    // Get all unique dates
    const allDates = new Set([
      ...growthData.userGrowth.map(item => item._id),
      ...growthData.petGrowth.map(item => item._id)
    ]);

    // Create combined data
    return Array.from(allDates).sort().map(date => ({
      date,
      users: userGrowthMap.get(date) || 0,
      pets: petGrowthMap.get(date) || 0,
    }));
  };

  const formatXAxisLabel = (tickItem: string) => {
    if (period === '1y') {
      // For yearly view, show month/year
      const [, month] = tickItem.split('-');
      return `${month}`;
    } else {
      // For daily views, show day/month
      const [, month, day] = tickItem.split('-');
      return `${day}/${month}`;
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: 420, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress size={40} thickness={4} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ height: 420, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const chartData = formatChartData();

  return (
    <Box sx={{ height: 420, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Büyüme Analizi
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {format(lastRefresh, 'HH:mm')}
          </Typography>
          <Tooltip title="Yenile">
            <IconButton size="small" onClick={() => fetchGrowthData(period)} disabled={loading} sx={{ p: 0.5 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select 
              value={period} 
              onChange={handlePeriodChange}
              sx={{ 
                fontSize: '0.875rem',
                '& .MuiSelect-select': { py: 0.5 }
              }}
            >
              <MenuItem value="7d">7 Gün</MenuItem>
              <MenuItem value="30d">30 Gün</MenuItem>
              <MenuItem value="90d">90 Gün</MenuItem>
              <MenuItem value="1y">1 Yıl</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="1 1" stroke="#f0f0f0" />
          <XAxis 
            dataKey="date" 
            tickFormatter={formatXAxisLabel}
            angle={-45}
            textAnchor="end"
            height={50}
            fontSize={11}
            stroke="#666"
          />
          <YAxis fontSize={11} stroke="#666" />
          <RechartsTooltip 
            labelFormatter={(label: any) => `Tarih: ${formatXAxisLabel(label)}`}
            formatter={(value: any, name: any) => [
              value, 
              name === 'users' ? 'Kullanıcılar' : 'Hayvanlar'
            ]}
            contentStyle={{
              borderRadius: 8,
              border: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          />
          <Legend 
            formatter={(value) => 
              value === 'users' ? 'Kullanıcılar' : 'Hayvanlar'
            }
            wrapperStyle={{ fontSize: '0.875rem' }}
          />
          <Line 
            type="monotone" 
            dataKey="users" 
            stroke="#667eea" 
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
          <Line 
            type="monotone" 
            dataKey="pets" 
            stroke="#43e97b" 
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default GrowthChart;
